import Hammer from 'hammerjs';
import cap from './cap';

/**
 * Hammer pan for panning and dragging on desktop and mobile, pan is working across devices.
 * Hammer pinch for zooming in on mobile, pinch is only working on mobile devices, not on desktop browsers
 *
 * @param {*} container The wrapper of the element to be zoomed.
 * The script will look for the first child of the container and apply the transforms to it.
 * @param {*} minScale The minimum scale (0.8 = 80% zoom)
 * @param {*} maxScale The maximum scale (4 = 400% zoom)
 * @param {*} isInitPan whether initializing panning and dragging or not
 * @param {*} targetLargerThanWrapper whether the size of the target is larger than wrapper
 */
export default function hammerIt(
    container,
    minScale = 1,
    maxScale = 4,
    isInitPan = true,
    targetLargerThanWrapper = true
) {
    const target = container.firstElementChild; // element to transform
    const hammertime = new Hammer(target);

    const pos = { x: 0, y: 0 }; // translate value
    const currentPos = { x: 0, y: 0 };
    let scale = 1;

    // dragging or panning area, avoid target dragging out of container
    const targetSize = { w: target.offsetWidth, h: target.offsetHeight };
    const containerSize = { w: container.offsetWidth, h: container.offsetHeight };

    // extra buffer for increasing dragging area
    const buffer = {
        x: Math.abs(Math.round(targetSize.w * minScale) - containerSize.w) / 2,
        y: Math.abs(Math.round(targetSize.h * minScale) - containerSize.h) / 2,
    };

    const rangeMax = { x: buffer.x, y: buffer.y };
    const rangeMin = { x: -buffer.x, y: -buffer.y };

    // check whether it's currently panning(dragging) or pinching(zoom in)
    let isPanning = false;
    let isPinching = false;

    // desktop zoom settings
    const factor = 0.02; // The zoom-speed (1 = +100% zoom per mouse wheel tick)
    const zoomPoint = { x: 0, y: 0 }; // position offset to its container
    const zoomTarget = { x: 0, y: 0 }; // position offset to its container
    const lastZoomPoint = { x: 0, y: 0 }; // position offset to its container

    // mobile pinch settings
    let currentScale = 1;
    let wait = 0; // After each pinch, it will execute extra pan. We avoid that.

    // update styles
    function updateTransform(p, s) {
        // const transform = 'translate(' + p.x + 'px,' + p.y + 'px) scale(' + s + ',' + s + ')';
        const transform = `translate(${p.x}px, ${p.y}px) scale(${s}, ${s})`;
        target.style.webkitTransform = transform;
        target.style.MozTransform = transform;
        target.style.msTransform = transform;
        target.style.OTransform = transform;
        target.style.transform = transform;
    }

    // panning and dragging range
    function updateRange() {
        let rangeOffsetX = Math.abs(Math.round(targetSize.w * currentScale) - containerSize.w) / 2;
        let rangeOffsetY = Math.abs(Math.round(targetSize.h * currentScale) - containerSize.h) / 2;
        if (targetLargerThanWrapper) {
            rangeOffsetX =
                Math.abs(
                    Math.round(targetSize.w * currentScale) - Math.round(targetSize.w * minScale)
                ) / 2;
            rangeOffsetY =
                Math.abs(
                    Math.round(targetSize.h * currentScale) - Math.round(targetSize.h * minScale)
                ) / 2;
        }
        rangeMax.x = rangeOffsetX + buffer.x;
        rangeMin.x = 0 - rangeMax.x;

        rangeMax.y = rangeOffsetY + buffer.y;
        rangeMin.y = 0 - rangeMax.y;

        if (rangeMax.x !== 0 && rangeMin.x !== 0 && rangeMax.y !== 0 && rangeMin.y !== 0) {
            target.style.cursor = 'grab';
        } else {
            target.style.cursor = 'default';
        }
    }

    // mouse wheel to trigger zoom in on desktop browsers
    // hold on ctrl key to capture the event
    function wheelZoom(e) {
        if (!isPanning && e.ctrlKey) {
            isPinching = true;
            e.preventDefault();
            zoomPoint.x = e.pageX - container.offsetLeft;
            zoomPoint.y = e.pageY - container.offsetTop;

            let delta = e.wheelDelta;
            if (delta === undefined) {
                // we are on firefox
                delta = e.detail;
            }

            // cap the delta to [-1,1] for cross browser consistency
            delta = cap(delta, -1, 1);

            // determine the point on where the slide is zoomed in
            zoomTarget.x = (zoomPoint.x + lastZoomPoint.x) / scale;
            zoomTarget.y = (zoomPoint.y + lastZoomPoint.y) / scale;

            // apply zoom
            scale += delta * factor * scale;
            scale = cap(scale, minScale, maxScale);
            currentScale = scale;

            // calculate x and y based on zoom
            pos.x += -zoomTarget.x * scale + zoomPoint.x;
            pos.y += -zoomTarget.y * scale + zoomPoint.y;

            // update the dragging range whenever the scale is changed
            updateRange();
            pos.x = cap(pos.x, rangeMin.x, rangeMax.x);
            pos.y = cap(pos.y, rangeMin.y, rangeMax.y);

            updateTransform(pos, scale);
            isPinching = false;
        }
    }

    // panning & dragging
    if (isInitPan) {
        hammertime.off('pan');
        hammertime.on('pan', (ev) => {
            if (!isPinching) {
                if (wait === 0) {
                    isPanning = true;
                    currentPos.x = cap(pos.x + ev.deltaX, rangeMin.x, rangeMax.x);
                    currentPos.y = cap(pos.y + ev.deltaY, rangeMin.y, rangeMax.y);
                    updateTransform(currentPos, scale);
                } else {
                    wait -= 1;
                }
            }
        });

        // pan end
        hammertime.on('panend pancancel', () => {
            // store values
            pos.x = currentPos.x;
            pos.y = currentPos.y;
            isPanning = false;
            wait = 1;
        });
    }

    // mobile pinch
    hammertime.off('pinch pinchmove');
    hammertime.on('pinch pinchmove', (ev) => {
        if (!isPanning) {
            isPinching = true;
            currentScale = cap(scale * ev.scale, minScale, maxScale);
            // update the dragging range whenever the scale is changed
            updateRange();
            currentPos.x = cap(pos.x + ev.deltaX, rangeMin.x, rangeMax.x);
            currentPos.y = cap(pos.y + ev.deltaY, rangeMin.y, rangeMax.y);
            updateTransform(currentPos, currentScale);
        }
    });

    // mobile pinch end
    hammertime.on('pinchend pinchcancel', () => {
        // store values
        scale = currentScale;
        pos.x = currentPos.x;
        pos.y = currentPos.y;
        isPinching = false;
        wait = 1;
    });

    // double tap to zoom in
    hammertime.off('doubletap');
    hammertime.on('doubletap', () => {
        currentScale = cap(scale * 2, minScale, maxScale);
        // update the dragging range whenever the scale is changed
        updateRange();
        if (scale !== currentScale) {
            target.animate(
                [
                    // keyframes
                    {
                        transform: `translate(${pos.x}px, ${pos.y}px)
                                        scale(${scale}, ${scale})`,
                    },
                    {
                        transform: `translate(${pos.x}px, ${pos.y}px)
                                        scale(${currentScale}, ${currentScale})`,
                    },
                ],
                { duration: 300, easing: 'ease' }
            );
            updateTransform(pos, currentScale);
            scale = currentScale;
        }
    });

    // detects trackpad zoom in and ctrl + mouse scroll on desktop
    target.removeEventListener('mousewheel', wheelZoom);
    target.removeEventListener('DOMMouseScroll', wheelZoom);
    target.addEventListener('mousewheel', wheelZoom);
    target.addEventListener('DOMMouseScroll', wheelZoom);

    hammertime.get('pinch').set({ enable: true });
    hammertime.get('pan').set({ enable: true, direction: Hammer.DIRECTION_ALL });
}
